
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem } from "../../components/Table/TableRow";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore } from "../_rootStore";

// MARK: Resources
import strings from "../../resources/strings";

export default class SurveyStore extends VariableChangeHandler {
	// Control
	@observable public loading: boolean = false;
	@observable public tableLoading: boolean = false;
	@observable public answerLoading: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public surveys: api.Survey[] = [];
	@observable public selectedSurvey: api.Survey | null = null;

	@observable public surveyStats: api.SurveyStats | null = null;

	@computed
	public get tableHeader() {
		const header = strings.surveys.table.header;
		return [
			header.name,
			header.email,
			header.nps,
		];
	}

	private static formatAnswers(survey: api.Survey): IRowItem {
		return {
			id: survey.id,
			name: survey.name,
			columns: [
				{
					value: survey.name,
				},
				{
					value: survey.email,
				},
				{
					value: survey.nps.toFixed(1).toString(),
				},
			],
		};
	}

	@computed
	public get tableAnswers(): IRowItem[] {
		return this.surveys.map(SurveyStore.formatAnswers);
	}

	@action
	public getStats = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.surveyStats = await api.getStats();
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getSurveys = async (pageOffSet?: number) => {
		if (this.tableLoading) {
			return;
		}

		this.tableLoading = true;

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		try {
			this.surveys = await api.getSurveys(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.tableLoading = false;
		}
	};

	@action
	public getSurvey = async (surveyId: string) => {
		if (this.answerLoading) {
			return;
		}

		this.answerLoading = true;

		try {
			this.selectedSurvey  = await api.getSurvey(surveyId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.answerLoading = false;
		}
	};

	@action
	public calcAverage = (answerSections: api.AnswerSection[]) => {
		let sumOfRates: number = 0;
		let numOfRates: number = 0;

		answerSections.forEach((answerSection) => {
			answerSection.answers.forEach((answer) => {
				sumOfRates += answer.rate;
				numOfRates += 1;
			});
		});

		return (sumOfRates / numOfRates);
	};

	@action
	public nextPage = async () => {
		await this.getSurveys(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getSurveys(this.pageOffSet - 1);
		}
	};

	// SurveyAnswerDialog
	@observable
	public surveyAnswerMessage: string = "";

	@observable
	public surveyAnswerOpen: boolean = false;

	@observable
	public surveyAnswerTitle: string = "";

	@action
	public openSurveyAnswerDialog = (surveyId: string) => {
		this.getSurvey(surveyId);
		this.surveyAnswerOpen = true;
	};

	@action
	public closeSurveyAnswerDialog = () => {
		this.surveyAnswerOpen = false;
		this.clearSurveyAnswerDialog();
	};

	@action
	public clearSurveyAnswerDialog = () => {
		this.surveyAnswerMessage = "";
		this.surveyAnswerTitle = "";
	}

}
